import type { DiGraph } from "./digraph.js";

/**
 * HITS: hub and authority scores on a directed link graph. Normalized L1 after each round.
 * hub[u] = sum of auth[in-neighbors], auth[v] = sum of hub[out-neighbors] from those edges.
 */
export function hits(
  g: DiGraph,
  options: { maxIter?: number; epsilon?: number } = {}
): { hub: Map<string, number>; authority: Map<string, number> } {
  const maxIter = options.maxIter ?? 100;
  const epsilon = options.epsilon ?? 1e-10;
  const nodes = g.nodes;
  const n = nodes.length;
  if (n === 0) {
    return { hub: new Map(), authority: new Map() };
  }

  const hub = new Map<string, number>();
  const auth = new Map<string, number>();
  const nInv = 1 / n;
  for (const v of nodes) {
    hub.set(v, nInv);
    auth.set(v, nInv);
  }

  for (let it = 0; it < maxIter; it++) {
    const authNext = new Map<string, number>();
    for (const v of nodes) {
      let s = 0;
      for (const { u } of g.inNeighbors(v)) {
        s += hub.get(u) ?? 0;
      }
      authNext.set(v, s);
    }
    let aSum = 0;
    for (const v of nodes) aSum += authNext.get(v) ?? 0;
    if (aSum < 1e-15) aSum = 1;
    for (const v of nodes) {
      authNext.set(v, (authNext.get(v) ?? 0) / aSum);
    }

    const hubNext = new Map<string, number>();
    for (const u of nodes) {
      let s = 0;
      for (const { v } of g.outNeighbors(u)) {
        s += authNext.get(v) ?? 0;
      }
      hubNext.set(u, s);
    }
    let hSum = 0;
    for (const u of nodes) hSum += hubNext.get(u) ?? 0;
    if (hSum < 1e-15) hSum = 1;
    for (const u of nodes) {
      hubNext.set(u, (hubNext.get(u) ?? 0) / hSum);
    }

    let diff = 0;
    for (const u of nodes) {
      diff += Math.abs((hub.get(u) ?? 0) - (hubNext.get(u) ?? 0));
    }
    for (const u of nodes) {
      hub.set(u, hubNext.get(u) ?? 0);
      auth.set(u, authNext.get(u) ?? 0);
    }
    if (diff < epsilon) break;
  }

  return { hub, authority: auth };
}
